import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Components
import { PageHeader } from "../components/PageSpecificComponents";
import { Button } from "../components/UI";
import AddExternalServiceModal from "../features/addExternalService/components/AddExternalServiceModal/AddExternalServiceModal";

// Page components
import { Schedule } from "../components/PageSpecificComponents/pagesComponents/employeeSchedule";

// Builders
import { defaultIncludedStatuses } from "../builders/employeeSchedule.builder";
import * as buttonDesignType from "../builders/buttonDesign.types";

// API
import { fetchWorkersRequest } from "../api/calls/services.call";

// Actions
import {
  getSchedule,
  getUnassignedSchedule,
  resetScheduleOnPageLeave,
  requestServicesRefetch
} from "../redux/actions/services.actions";

// Styles
import { EmployeeScheduleStyles } from "../pagesStyles/employeeSchedule.styles";

const dateToString = date => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const dd = day < 10 ? `0${day}` : `${day}`;
  const mm = month < 10 ? `0${month}` : `${month}`;
  return `${year}-${mm}-${dd}`;
};

const EmployeeSchedule = () => {
  const dispatch = useDispatch();
  const { shouldRefetchServicesAfterAction } = useSelector(
    state => state.servicesReducer
  );
  const { activeBranch } = useSelector(state => state.app);

  const [dateQuery, setDateQuery] = useState(new Date());
  const [includedStatuses, setIncludedStatuses] = useState(
    defaultIncludedStatuses
  );
  // Start with the full branch worker list (auto-populated below) so the
  // schedule defaults to "everyone visible" per the redesign.
  const [branchWorkers, setBranchWorkers] = useState([]);
  // The team rail's checkbox state — drives both schedule fetch (workers[])
  // and which workers actually render in the timeline.
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);

  const [isExternalModalOpen, setIsExternalModalOpen] = useState(false);

  // The form posts /services/external directly (no saga), so we kick off
  // a schedule + unassigned refetch here. Fires on cancel too — that
  // costs a single extra fetch, which beats threading a success callback
  // through the form just to tell apart cancel from save.
  const handleExternalModalClose = () => {
    setIsExternalModalOpen(false);
    dispatch(requestServicesRefetch());
  };

  // Load every worker for the branch on mount + on branch change.
  // The existing `/services/schedule` endpoint requires a non-empty
  // workers[] param, so we pre-populate the selection here.
  //
  // On every branch switch we also wipe the previous branch's schedule
  // (employees, workDay, unassignedServices) immediately so the user
  // never sees a flash of stale data while the new branch's worker list
  // is in flight — and so an empty new branch shows the correct empty
  // state instead of the previous branch's rows.
  useEffect(() => {
    let cancelled = false;
    dispatch(resetScheduleOnPageLeave());
    setBranchWorkers([]);
    setSelectedWorkerIds([]);
    const loadWorkers = async () => {
      const response = await fetchWorkersRequest();
      if (cancelled) return;
      if (response.hasError) {
        toast.error(response.error.message);
        return;
      }
      setBranchWorkers(response.workers || []);
      setSelectedWorkerIds((response.workers || []).map(w => w.id));
    };
    loadWorkers();
    return () => {
      cancelled = true;
    };
  }, [activeBranch, dispatch]);

  const dateString = dateToString(dateQuery);

  const refetch = useCallback(() => {
    // Unassigned fetch never depends on workers — branches with no staff still
    // need to surface incoming tickets so the manager can act on them.
    dispatch(getUnassignedSchedule(dateString));

    // Schedule (workers + their services) only fetches when at least one
    // worker is selected; the BE requires a non-empty workers[] filter.
    if (selectedWorkerIds.length === 0) return;
    const chosenEmployees = branchWorkers.filter(w =>
      selectedWorkerIds.includes(w.id)
    );
    dispatch(getSchedule(dateString, includedStatuses, chosenEmployees));
  }, [
    dateString,
    includedStatuses,
    selectedWorkerIds,
    branchWorkers,
    dispatch
  ]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Reset only on unmount (and on branch change, handled by the effect
  // above). Tying the reset to the refetch effect's cleanup made every
  // status-filter / worker-selection change wipe `workDay` to undefined
  // for a frame, which collapsed the timeline to the 6 AM–10 PM fallback
  // and visibly jumped the hour labels and now-line.
  useEffect(() => {
    return () => {
      dispatch(resetScheduleOnPageLeave());
    };
  }, [dispatch]);

  useEffect(() => {
    if (shouldRefetchServicesAfterAction === "refetch") {
      refetch();
    }
  }, [shouldRefetchServicesAfterAction, refetch]);

  return (
    <EmployeeScheduleStyles>
      <PageHeader title="Employee Schedule">
        <Button
          buttonDesignType={buttonDesignType.PRIMARY}
          onClick={() => setIsExternalModalOpen(true)}
        >
          + Add External Service
        </Button>
      </PageHeader>
      <Schedule
        branchWorkers={branchWorkers}
        timeZone={activeBranch?.timeZone}
        dateQuery={dateQuery}
        setDateQuery={setDateQuery}
        includedStatuses={includedStatuses}
        setIncludedStatuses={setIncludedStatuses}
        selectedWorkerIds={selectedWorkerIds}
        setSelectedWorkerIds={setSelectedWorkerIds}
      />

      {isExternalModalOpen && (
        <AddExternalServiceModal closeModal={handleExternalModalClose} />
      )}
    </EmployeeScheduleStyles>
  );
};

export default EmployeeSchedule;
